import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import { useInfiniteVideos } from "@/lib/useInfinite";
import { ScrollSentinel } from "@/components/ScrollSentinel";
import {
  Sparkles, TrendingUp, Clock, Film, ChevronRight, Flame, Music, Gamepad2,
  Smile, GraduationCap, Clapperboard, Trophy, Utensils, Users, Star, MoreHorizontal
} from "lucide-react";

const CATEGORY_META = {
  All: { icon: Clapperboard, color: "#00E7FF", tag: "Everything, everywhere" },
  Trending: { icon: Flame, color: "#FF6B00", tag: "What the world's watching" },
  Music: { icon: Music, color: "#B84BFF", tag: "Beats, bangers & live sets" },
  Gaming: { icon: Gamepad2, color: "#22C55E", tag: "Streams, clips & speedruns" },
  Comedy: { icon: Smile, color: "#FFD700", tag: "Laughs on tap" },
  Education: { icon: GraduationCap, color: "#FF6D2E", tag: "Learn something new" },
  Featured: { icon: Star, color: "#B4A6FF", tag: "Hand-picked by Jixxeo" },
  Tech: { icon: Sparkles, color: "#00E7FF", tag: "Devices, code & the future" },
  Sports: { icon: Trophy, color: "#FF3EA5", tag: "Highlights & moves" },
  Vlogs: { icon: Users, color: "#00E7FF", tag: "A day in the life" },
  Fitness: { icon: Flame, color: "#FF6B00", tag: "Move. Sweat. Repeat." },
  Food: { icon: Utensils, color: "#FFD700", tag: "Bites, recipes & feasts" },
  General: { icon: Clapperboard, color: "#00E7FF", tag: "The best of Jixxeo" },
};

export default function Category() {
  const { name } = useParams();
  const cat = decodeURIComponent(name || "General");
  const meta = CATEGORY_META[cat] || { icon: MoreHorizontal, color: "#00E7FF", tag: `#${cat}` };
  const Icon = meta.icon;

  const [trending, setTrending] = useState([]);
  const [loops, setLoops] = useState([]);
  const [loading, setLoading] = useState(true);
  const freshParams = cat === "All" ? { sort: "new", format: "long" } : { category: cat, sort: "new", format: "long" };
  const { items: fresh, loading: freshLoading, done: freshDone, sentinelRef } = useInfiniteVideos(freshParams, 12);

  useEffect(() => {
    setLoading(true);
    const params = cat === "All" ? {} : { category: cat };
    Promise.all([
      api.get("/videos", { params: { ...params, sort: "trending", format: "long", limit: 8 } }).then(r => setTrending(r.data)),
      api.get("/videos", { params: { ...params, format: "short", limit: 10 } }).then(r => setLoops(r.data)),
    ]).finally(() => setLoading(false));
  }, [cat]);

  const empty = !loading && !freshLoading && trending.length === 0 && fresh.length === 0 && loops.length === 0;

  return (
    <div className="mx-auto max-w-7xl" data-testid={`category-page-${cat}`}>
      {/* HERO */}
      <section className="px-3 sm:px-6 pt-4">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(0,231,255,0.12)]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f0b34] via-[#1a0f4a] to-[#0d0b2b]"/>
          <div className="absolute inset-0" style={{background: `radial-gradient(600px 300px at 85% 40%, ${meta.color}55, transparent 60%), radial-gradient(500px 300px at 10% 20%, #ff3ea555, transparent 60%)`}}/>
          <div className="relative p-5 sm:p-8 flex items-center gap-5 sm:gap-8">
            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl bg-black/50 border border-white/10 flex items-center justify-center shrink-0" style={{boxShadow: `0 0 32px ${meta.color}66`}}>
              <Icon size={40} style={{color: meta.color}}/>
            </div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm font-bold tracking-widest text-white/60">CATEGORY</div>
              <h1 className="h-font text-3xl sm:text-5xl font-extrabold truncate" style={{color: meta.color}}>{cat}</h1>
              <p className="text-white/70 text-sm sm:text-base mt-1">{meta.tag}</p>
            </div>
          </div>
        </div>
      </section>

      {loading && <div className="p-8 text-white/60 text-sm">Loading {cat}…</div>}
      {empty && (
        <div className="mx-3 sm:mx-6 mt-8 p-8 rounded-2xl bg-white/5 border border-white/10 text-center">
          <div className="h-font text-xl font-bold">No videos in {cat} yet</div>
          <p className="text-white/60 text-sm mt-1">Be the first to upload something great.</p>
          <Link to="/upload" className="jx-btn inline-block mt-4 h-11 px-6 rounded-full bg-[#00E7FF] text-black font-extrabold">Upload now</Link>
        </div>
      )}

      {trending.length > 0 && (
        <Section title={`Trending in ${cat}`} icon={TrendingUp} testid="cat-trending" iconColor="#FF6B00" href={`/search?sort=trending`}>
          {trending.map(v => <VideoCard key={v.id} v={v}/>)}
        </Section>
      )}

      {loops.length > 0 && (
        <section className="px-3 sm:px-6 mt-10" data-testid="cat-loops-row">
          <div className="flex items-end justify-between mb-3">
            <h2 className="h-font text-2xl font-bold flex items-center gap-2"><Film size={20} className="text-[#FF3EA5]"/> {cat} Loops</h2>
            <Link to="/shorts" className="text-xs font-semibold text-[#00E7FF] inline-flex items-center gap-1">See All <ChevronRight size={12}/></Link>
          </div>
          <div className="flex gap-3 overflow-x-auto jx-scroll-x -mx-3 sm:-mx-6 px-3 sm:px-6">
            {loops.map(s => (
              <Link key={s.id} to={`/shorts?v=${s.id}`} className="shrink-0 w-40 sm:w-44">
                <div className="aspect-[9/16] rounded-2xl overflow-hidden bg-black relative border border-white/5">
                  <img src={s.thumbnail_url || undefined} alt={s.title} className="w-full h-full object-cover"/>
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[10px] font-bold text-[#FF3EA5] flex items-center gap-1"><Film size={10}/> LOOP</div>
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                    <p className="text-xs font-semibold line-clamp-2">{s.title}</p>
                    <p className="text-[10px] text-white/60">@{s.creator?.username}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {fresh.length > 0 && (
        <Section title={`Fresh in ${cat}`} icon={Clock} testid="cat-fresh" iconColor="#00E7FF">
          {fresh.map(v => <VideoCard key={v.id} v={v}/>)}
        </Section>
      )}
      <ScrollSentinel sentinelRef={sentinelRef} loading={freshLoading} done={freshDone} count={fresh.length} testid="cat-fresh-sentinel"/>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children, iconColor, href }) {
  return (
    <section className="px-3 sm:px-6 mt-10" data-testid={`section-${testid}`}>
      <div className="flex items-end justify-between mb-4">
        <h2 className="h-font text-2xl font-bold flex items-center gap-2">
          <Icon size={22} style={{color: iconColor || "#00E7FF"}}/> {title}
        </h2>
        {href && <Link to={href} className="text-sm font-semibold text-[#00E7FF] inline-flex items-center gap-1">See All <ChevronRight size={14}/></Link>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">{children}</div>
    </section>
  );
}
